import { useEffect, useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createProperty } from '../api/properties';
import axiosInstance from '../api/axiosInstance';
import { fetchUsageSafe } from '../api/usage';
import { useSubmissionLock } from '../hooks/useSubmissionLock';

const initialState = {
  title: '',
  address1: '',
  town: '',
  county: '',
  postcode: '',
  type: '',
  status: '',
  description: '',
  purchasePrice: '',
  rentalCost: '',
  image: null,

  // ✅ EPC fields
  epcRating: '',
  epcExpiry: '',
  epcUrl: '',

  // ✅ HMO helper (frontend-only; not sent to backend as a property field)
  roomCount: '',
};

const propertyTypes = [
  { value: '', label: 'Select type' },
  { value: 'house', label: 'House' },
  { value: 'flat', label: 'Flat' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'hmo', label: 'HMO / Mixed Use' },
  { value: 'commercial', label: 'Commercial' },
];

const propertyStatuses = [
  { value: '', label: 'Select status' },
  { value: 'vacant', label: 'Vacant' },
  { value: 'occupied', label: 'Occupied' },
];

function propertyLimitCopy(usage) {
  const target = usage?.isPlus && !usage?.isEnhanced ? 'Beem Enhanced' : 'Beem Plus';
  return `Property limit reached (${usage?.propertyLimit ?? 0}). Upgrade to ${target} to add more properties.`;
}

export default function AddPropertyDrawer({ isOpen, onClose, onSave, onCreate }) {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [usage, setUsage] = useState(null);
  const { run: runSubmission, isPending } = useSubmissionLock();
  const navigate = useNavigate();

  const authHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchUsageSafe().then(setUsage);
  }, [isOpen]);

  if (!isOpen) return null;

  const propertyLimitReached =
    usage && typeof usage.propertyCount === 'number' && typeof usage.propertyLimit === 'number'
      ? usage.propertyCount >= usage.propertyLimit
      : false;

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image' && files?.[0]) {
      setForm((prev) => ({ ...prev, image: files[0] }));
      setImagePreview(URL.createObjectURL(files[0]));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const isHmo = (t) => (t || '').toString().toLowerCase() === 'hmo';

  const createRoomsIfNeeded = async (propertyId) => {
    const countRaw = form.roomCount;
    const count = countRaw === '' ? 0 : Number(countRaw);

    if (!propertyId) return;
    if (!isHmo(form.type)) return;
    if (!Number.isFinite(count) || count <= 0) return;

    // Create Room 1..Room N (best-effort)
    for (let i = 1; i <= count; i++) {
      try {
        await axiosInstance.post(
          `/api/properties/${propertyId}/rooms`,
          { name: `Room ${i}` },
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
          }
        );
      } catch (err) {
        console.warn(`[AddPropertyDrawer] failed to create Room ${i}`, err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || isPending('property:create')) return;

    // Paywall safeguard (backend also enforces)
    if (propertyLimitReached) {
      alert(propertyLimitCopy(usage));
      return;
    }

    await runSubmission('property:create', async () => {
      setSaving(true);
      console.log('[AddPropertyDrawer] handleSubmit start', { hasOnCreate: !!onCreate, hasOnSave: !!onSave });

      try {
      const formData = new FormData();

      for (const [key, value] of Object.entries(form)) {
        if (value === null || value === '') continue;

        // roomCount is frontend-only and image is appended explicitly below.
        if (key === 'roomCount' || key === 'image') continue;

        if (key === 'purchasePrice' || key === 'rentalCost') {
          formData.append(key, parseFloat(value));
        } else {
          formData.append(key, value);
        }
      }

      if (form.image instanceof File) {
        formData.append('image', form.image, form.image.name);
      }

      let created = null;

      try {
        const res = await createProperty(formData);
        created = res?.data ?? res ?? null;
        console.log('[AddPropertyDrawer] createProperty returned:', created);

        // ✅ If HMO + roomCount set, create rooms immediately (best effort)
        await createRoomsIfNeeded(created?.id);

        // Refresh usage after successful create
        fetchUsageSafe().then(setUsage);
      } catch (apiErr) {
        console.error('[AddPropertyDrawer] createProperty API error', apiErr);

        const code = apiErr?.response?.data?.code;

        // If backend says limit reached, do NOT create fallback
        if (code === 'PROPERTY_LIMIT_REACHED') {
          const limit = apiErr?.response?.data?.limit ?? usage?.propertyLimit ?? 0;
          alert(propertyLimitCopy({ ...usage, propertyLimit: limit }));
          fetchUsageSafe().then(setUsage);
          return;
        }

        const responseData = apiErr?.response?.data;
        const message =
          responseData?.error || 'Failed to create property. Please try again.';
        const storageMessage = responseData?.storageError?.message;
        alert(storageMessage ? `${message}: ${storageMessage}` : message);
        return;
      }

      const parentHandler = onCreate ?? onSave;
      if (typeof parentHandler === 'function') {
        try {
          const maybe = parentHandler(created);
          if (maybe && typeof maybe.then === 'function') {
            await maybe;
          }
          console.log('[AddPropertyDrawer] parent handler finished');
        } catch (err) {
          console.warn('[AddPropertyDrawer] parent handler threw', err);
        }
      } else {
        console.log('[AddPropertyDrawer] no parent handler provided (will navigate after save)');
      }

      try {
        window.dispatchEvent(new CustomEvent('properties:updated', { detail: { created } }));
        console.log('[AddPropertyDrawer] dispatched properties:updated');
      } catch (err) {
        console.warn('[AddPropertyDrawer] dispatch event failed', err);
      }

      try {
        localStorage.setItem('properties:updated', JSON.stringify({ created, ts: Date.now() }));
      } catch {
        // ignore
      }

      setForm(initialState);
      setImagePreview(null);

      try {
        onClose?.();
        console.log('[AddPropertyDrawer] onClose called');
      } catch (err) {
        console.warn('[AddPropertyDrawer] onClose threw', err);
      }

      if (!parentHandler) {
        navigate('/properties');
      }
      } catch (err) {
        console.error('[AddPropertyDrawer] unexpected error', err);
      } finally {
        setSaving(false);
        console.log('[AddPropertyDrawer] handleSubmit finished');
      }
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto h-full w-full max-w-xl overflow-y-auto bg-[linear-gradient(to_bottom,_#2A2A2A,_#1A1A1A)] p-8 text-white">
        <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-[#AF9B8A]">
          <X className="w-6 h-6" />
        </button>
        <h2 className="text-2xl font-bold mb-6">Add Property</h2>

        {/* Paywall note (no UI redesign, just info) */}
        {propertyLimitReached && (
          <div className="mb-4 p-3 rounded-lg bg-white/10 border border-white/10 text-white/80 text-sm">
            {propertyLimitCopy(usage)}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block mb-1 font-semibold">Title</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              placeholder="Property title"
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold">Address</label>
            <div className="flex gap-4">
              <input
                type="text"
                name="address1"
                value={form.address1}
                onChange={handleChange}
                required
                placeholder="Street"
                className="flex-1 px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              />
              <input
                type="text"
                name="town"
                value={form.town}
                onChange={handleChange}
                required
                placeholder="Town"
                className="flex-1 px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              />
            </div>
            <div className="flex gap-4 mt-4">
              <input
                type="text"
                name="county"
                value={form.county}
                onChange={handleChange}
                required
                placeholder="County"
                className="flex-1 px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              />
              <input
                type="text"
                name="postcode"
                value={form.postcode}
                onChange={handleChange}
                required
                placeholder="Postcode"
                className="flex-1 px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1 relative">
              <label className="block mb-1 font-semibold">Type</label>
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                required
                className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/20 border border-white/10"
              >
                {propertyTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-10 w-4 h-4 text-white/70 pointer-events-none" />
            </div>
            <div className="flex-1 relative">
              <label className="block mb-1 font-semibold">Status</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                required
                className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/20 border border-white/10"
              >
                {propertyStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-10 w-4 h-4 text-white/70 pointer-events-none" />
            </div>
          </div>

          {/* ✅ HMO helper: number of rooms (minimal) */}
          {isHmo(form.type) && (
            <div>
              <label className="block mb-1 font-semibold">Number of rooms</label>
              <input
                type="number"
                name="roomCount"
                min="0"
                value={form.roomCount}
                onChange={handleChange}
                placeholder="e.g. 5"
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              />
              <p className="text-xs text-white/60 mt-2">
                This will auto-create Room 1..Room N after the property is created.
              </p>
            </div>
          )}

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block mb-1 font-semibold">Purchase Price (£)</label>
              <input
                type="number"
                name="purchasePrice"
                value={form.purchasePrice}
                onChange={handleChange}
                placeholder="e.g. 250000"
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              />
            </div>
            <div className="flex-1">
              <label className="block mb-1 font-semibold">Rental Cost (£/month)</label>
              <input
                type="number"
                name="rentalCost"
                value={form.rentalCost}
                onChange={handleChange}
                placeholder="e.g. 1600"
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
              />
            </div>
          </div>

          {/* ✅ EPC inputs */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block mb-1 font-semibold">EPC Rating</label>
              <select
                name="epcRating"
                value={form.epcRating}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
              >
                <option value="">Select rating</option>
                {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block mb-1 font-semibold">EPC Expiry</label>
              <input
                type="date"
                name="epcExpiry"
                value={form.epcExpiry}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold">EPC Certificate URL</label>
            <input
              type="url"
              name="epcUrl"
              value={form.epcUrl}
              onChange={handleChange}
              placeholder="https://..."
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder="Property description"
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400"
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold">Upload Image</label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleChange}
                className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#AF9B8A]/20 file:text-white hover:file:bg-[#AF9B8A]/40"
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-16 h-16 object-cover rounded-lg border border-white/10"
                />
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={saving || propertyLimitReached}
            className="w-full py-3 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? 'Adding property...' : 'Add Property'}
          </button>
        </form>
      </div>
    </div>
  );
}
